import { nf } from "../../utilities/Utils";

const toNumber = (value) => {
  const numericValue = Number(value ?? 0);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const getScaledWidth = (value, maxValue) => {
  if (maxValue <= 0 || value <= 0) {
    return 0;
  }

  const ratio = Math.min(value / maxValue, 1);

  return Math.max(Math.pow(ratio, 0.62) * 100, 1.5);
};

export const getCountValue = (item, keys = ["value", "contact_count", "count"]) => {
  const key = keys.find((candidate) => item?.[candidate] !== undefined && item?.[candidate] !== null);
  return toNumber(key ? item[key] : 0);
};

export const getSourceLabel = (item) => {
  const label = item?.source_name ?? item?.label ?? item?.name ?? item?.state_name ?? "Sin dato";
  return String(label) === "0" ? "Sin dato" : label;
};

export const getBrandLabel = (item) => {
  if (item?.brand_id === 0 || item?.brand_id === "0" || item?.brand_name === "0" || !item?.brand_name) {
    return "Sin Marca";
  }

  return item.brand_name;
};

export const sortByValue = (items = [], keys) => (
  [...items].sort((a, b) => getCountValue(b, keys) - getCountValue(a, keys))
);

export const DashboardMetric = ({ label, value, detail, accent = "red" }) => (
  <article className={`dashboard-metric dashboard-metric--${accent}`}>
    <span className="dashboard-metric__label">{label}</span>
    <strong className="dashboard-metric__value">{nf(value)}</strong>
    {detail ? <span className="dashboard-metric__detail">{detail}</span> : null}
  </article>
);

export const DashboardPanel = ({ kicker, title, children, className = "" }) => (
  <article className={`dashboard-panel ${className}`.trim()}>
    <div className="dashboard-panel__header">
      <div>
        {kicker ? <span className="dashboard-panel__kicker">{kicker}</span> : null}
        <h3 className="dashboard-panel__title">{title}</h3>
      </div>
    </div>
    {children}
  </article>
);

export const DashboardBars = ({
  items = [],
  getLabel = (item) => item?.label ?? item?.name ?? "Sin dato",
  getValue = (item) => getCountValue(item),
  emptyLabel = "Sin datos",
  limit,
  tone = "red",
  valueSuffix = "",
}) => {
  const visibleItems = limit ? items.slice(0, limit) : items;
  const maxValue = Math.max(...visibleItems.map((item) => getValue(item)), 0);

  if (!visibleItems.length) {
    return <div className="dashboard-empty">{emptyLabel}</div>;
  }

  return (
    <div className={`dashboard-distribution dashboard-distribution--${tone}`}>
      {visibleItems.map((item, index) => {
        const value = getValue(item);
        const width = maxValue > 0 ? Math.max((value / maxValue) * 100, 4) : 0;
        const rowKey = item?.key ?? item?.id ?? item?.source_id ?? item?.brand_id ?? item?.state_name ?? `${getLabel(item)}-${index}`;

        return (
          <div className="dashboard-distribution__row dashboard-detail-row" key={rowKey}>
            <div className="dashboard-distribution__meta">
              <span className="dashboard-distribution__label">{getLabel(item)}</span>
              <span className="dashboard-distribution__value">{nf(value)}{valueSuffix}</span>
            </div>
            <div className="dashboard-distribution__track">
              <div className="dashboard-distribution__fill" style={{ width: `${width}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const DashboardSplitBars = ({
  items = [],
  getLabel = getSourceLabel,
  dogKey = "dog_count",
  catKey = "cat_count",
  emptyLabel = "Sin datos",
  limit,
}) => {
  const visibleItems = limit ? items.slice(0, limit) : items;
  const maxValue = Math.max(
    ...visibleItems.map((item) => Math.max(toNumber(item?.[dogKey] ?? item?.dogs), toNumber(item?.[catKey] ?? item?.cats))),
    0
  );

  if (!visibleItems.length) {
    return <div className="dashboard-empty">{emptyLabel}</div>;
  }

  return (
    <div className="dashboard-split-list">
      {visibleItems.map((item, index) => {
        const dogs = toNumber(item?.[dogKey] ?? item?.dogs);
        const cats = toNumber(item?.[catKey] ?? item?.cats);
        const dogWidth = getScaledWidth(dogs, maxValue);
        const catWidth = getScaledWidth(cats, maxValue);
        const rowKey = item?.key ?? item?.id ?? item?.source_id ?? item?.state_name ?? `${getLabel(item)}-${index}`;

        return (
          <div className="dashboard-split-row" key={rowKey}>
            <div className="dashboard-split-row__label">{getLabel(item)}</div>
            <div className="dashboard-split-row__metrics">
              <span>Perros {nf(dogs)}</span>
              <span>Gatos {nf(cats)}</span>
            </div>
            <div className="dashboard-split-row__tracks">
              <div className="dashboard-split-row__track">
                <div className="dashboard-split-row__fill dashboard-split-row__fill--dog" style={{ width: `${dogWidth}%` }} />
              </div>
              <div className="dashboard-split-row__track">
                <div className="dashboard-split-row__fill dashboard-split-row__fill--cat" style={{ width: `${catWidth}%` }} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
